import { useEffect, useRef, useState } from "react";
import { formatDistanceToNow } from "date-fns";
import { useFetcher, useRevalidator } from "react-router";

import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { JudgeCredentialsDialog } from "@/components/JudgeCredentialsDialog";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Settings,
  CheckCircle,
  Edit,
  Copy,
  Globe,
  Lock,
  Trash2,
  Check,
  X,
  Pencil,
  Play,
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { Switch } from "@/components/ui/switch";
import { useAuthenticatedUser } from "@/hooks/useAuthenticatedUser";
import { useWorkspaceViewContext } from "@/hooks/useWorkspaceViewContext";
import type { Judge } from "@/lib/judges";
import {
  WORKSPACE_SIDEBAR_ACTION_PATH,
  workspaceSidebarAction,
} from "@/routes/workspaceSidebarAction";
import type {
  WorkspaceSidebarActionIntent,
  WorkspaceSidebarActionResult,
} from "@/routes/workspaceSidebarAction";
import type { FetcherWithComponents } from "react-router";
import {
  formatSupabaseFunctionError,
  parseSupabaseFunctionError,
} from "@shared/functions/supabaseFunctionError.ts";
import { supabase } from "@shared/integrations/supabase/client";
import { invokeFunction } from "@shared/functions.ts";
import JudgeTesterContract from "@shared/functions/judge-tester.ts";
import {
  juryConfigSchema,
  type JudgeTesterRequest,
  type JudgeTesterOutput,
} from "@shared/judge/schema";

export type { Judge } from "@/lib/judges";

interface JudgeViewProps {
  judge: Judge;
  userEmail?: string | undefined;
  onUpdate?: () => void;
  onDelete?: () => void;
}

export const JudgeView = ({
  judge,
  userEmail,
  onUpdate,
  onDelete,
}: JudgeViewProps) => {
  const user = useAuthenticatedUser();
  const { judges: availableJudges } = useWorkspaceViewContext();
  const initialJuryConfig = (() => {
    const parsed = juryConfigSchema.safeParse(judge.jury_config);
    return parsed.success ? parsed.data : null;
  })();
  const initialMemberJudgeIds = (initialJuryConfig?.memberJudgeIds ?? []).filter(
    (id) => id !== judge.id,
  );
  const recommendedMajorityCount =
    initialMemberJudgeIds.length > 0
      ? Math.floor(initialMemberJudgeIds.length / 2) + 1
      : 1;
  const [credentialsDialogOpen, setCredentialsDialogOpen] = useState(false);
  const [systemPromptDialogOpen, setSystemPromptDialogOpen] = useState(false);
  const [duplicateDialogOpen, setDuplicateDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [editedTitle, setEditedTitle] = useState(judge.title);
  const [systemPrompt, setSystemPrompt] = useState(judge.system_prompt || "");
  const [duplicateJudgeName, setDuplicateJudgeName] = useState(
    `${judge.title} (Copy)`,
  );
  const [isPublic, setIsPublic] = useState(judge.is_public);
  const [pendingVisibility, setPendingVisibility] = useState<boolean | null>(
    null,
  );
  const [juryEnabled, setJuryEnabled] = useState(
    initialJuryConfig?.enabled ?? false,
  );
  const [juryMemberSeatCounts, setJuryMemberSeatCounts] = useState<
    Record<string, number>
  >(() => {
    const counts: Record<string, number> = {};
    for (const id of initialMemberJudgeIds) {
      counts[id] = (counts[id] ?? 0) + 1;
    }
    return counts;
  });
  const [juryMajorityCount, setJuryMajorityCount] = useState(
    initialJuryConfig?.majorityCount ?? recommendedMajorityCount,
  );
  const [juryMaxConsults, setJuryMaxConsults] = useState(
    initialJuryConfig?.maxConsults ??
      (initialMemberJudgeIds.length > 0 ? initialMemberJudgeIds.length : 1),
  );
  const [testInput, setTestInput] = useState("");
  const [testOutput, setTestOutput] = useState("");
  const [testExpected, setTestExpected] = useState("");
  const [testRule, setTestRule] = useState("");
  const [testResult, setTestResult] = useState<JudgeTesterOutput | null>(null);
  const [testError, setTestError] = useState<string | null>(null);
  const [isTesting, setIsTesting] = useState(false);
  const { toast } = useToast();
  const revalidator = useRevalidator();
  const systemPromptFetcher = useFetcher<typeof workspaceSidebarAction>();
  const duplicateFetcher = useFetcher<typeof workspaceSidebarAction>();
  const visibilityFetcher = useFetcher<typeof workspaceSidebarAction>();
  const juryConfigFetcher = useFetcher<typeof workspaceSidebarAction>();
  const titleFetcher = useFetcher<typeof workspaceSidebarAction>();
  const deleteFetcher = useFetcher<typeof workspaceSidebarAction>();
  const systemPromptResultRef = useRef<WorkspaceSidebarActionResult | null>(
    null,
  );
  const duplicateResultRef = useRef<WorkspaceSidebarActionResult | null>(null);
  const visibilityResultRef = useRef<WorkspaceSidebarActionResult | null>(null);
  const juryConfigResultRef = useRef<WorkspaceSidebarActionResult | null>(null);
  const titleResultRef = useRef<WorkspaceSidebarActionResult | null>(null);
  const deleteResultRef = useRef<WorkspaceSidebarActionResult | null>(null);
  const pendingTitleRef = useRef<string | null>(null);
  const isSystemPromptPending = systemPromptFetcher.state !== "idle";
  const isDuplicatePending = duplicateFetcher.state !== "idle";
  const isVisibilityPending = visibilityFetcher.state !== "idle";
  const isJuryConfigPending = juryConfigFetcher.state !== "idle";
  const isTitlePending = titleFetcher.state !== "idle";
  const isDeletePending = deleteFetcher.state !== "idle";

  const isOwner = user?.id === judge.user_id;

  const submitJudgeAction = (
    fetcher: FetcherWithComponents<WorkspaceSidebarActionResult>,
    intent: WorkspaceSidebarActionIntent,
    fields: Record<string, string>,
  ) => {
    const formData = new FormData();
    formData.set("intent", intent);
    Object.entries(fields).forEach(([key, value]) => formData.set(key, value));
    fetcher.submit(formData, {
      method: "post",
      action: WORKSPACE_SIDEBAR_ACTION_PATH,
    });
  };

  const handleSystemPromptSave = () => {
    submitJudgeAction(systemPromptFetcher, "judgeUpdateSystemPrompt", {
      entityId: judge.id,
      systemPrompt,
    });
  };

  const handleDuplicateJudge = () => {
    submitJudgeAction(duplicateFetcher, "judgeDuplicate", {
      title: duplicateJudgeName,
      model: judge.model ?? "openai/gpt-4o-mini",
      max_tokens: judge.max_tokens?.toString() ?? "",
      temperature: judge.temperature?.toString() ?? "",
      system_prompt: judge.system_prompt ?? "",
    });
  };

  const handlePublicToggle = (checked: boolean) => {
    setPendingVisibility(checked);
    submitJudgeAction(visibilityFetcher, "judgeTogglePublic", {
      entityId: judge.id,
      isPublic: String(checked),
    });
  };

  const setJuryMemberSeatCount = (memberId: string, rawCount: number) => {
    const nextCount = Math.max(0, Math.trunc(rawCount));
    setJuryMemberSeatCounts((prev) => {
      if (nextCount <= 0) {
        if (!(memberId in prev)) return prev;
        const { [memberId]: _, ...rest } = prev;
        return rest;
      }
      return { ...prev, [memberId]: nextCount };
    });
  };

  const handleSaveJuryConfig = () => {
    if (!isOwner) return;

    const memberJudgeIds = Object.entries(juryMemberSeatCounts)
      .filter(([id]) => id !== judge.id)
      .flatMap(([id, rawCount]) => {
        const count = Math.max(0, Math.trunc(rawCount));
        if (!Number.isFinite(count) || count <= 0) {
          return [];
        }
        return Array.from({ length: count }, () => id);
      });
    const majorityCount = Math.trunc(juryMajorityCount);
    const maxConsults = Math.trunc(juryMaxConsults);

    if (!Number.isFinite(majorityCount) || majorityCount < 1) {
      toast({
        title: "Invalid configuration",
        description: "Majority count must be a positive integer",
        variant: "destructive",
      });
      return;
    }

    if (!Number.isFinite(maxConsults) || maxConsults < 1) {
      toast({
        title: "Invalid configuration",
        description: "Max consults must be a positive integer",
        variant: "destructive",
      });
      return;
    }

    if (juryEnabled) {
      if (memberJudgeIds.length === 0) {
        toast({
          title: "Invalid configuration",
          description: "Select at least one member seat",
          variant: "destructive",
        });
        return;
      }

      if (memberJudgeIds.length % 2 === 0) {
        toast({
          title: "Invalid configuration",
          description: "Member seat count must be odd",
          variant: "destructive",
        });
        return;
      }

      if (majorityCount <= memberJudgeIds.length / 2) {
        toast({
          title: "Invalid configuration",
          description: "Majority count must be a strict majority",
          variant: "destructive",
        });
        return;
      }

      if (maxConsults < majorityCount || maxConsults > memberJudgeIds.length) {
        toast({
          title: "Invalid configuration",
          description:
            "Max consults must satisfy majorityCount <= maxConsults <= member count",
          variant: "destructive",
        });
        return;
      }
    }

    const config = {
      enabled: juryEnabled,
      memberJudgeIds,
      majorityCount,
      maxConsults,
    };

    submitJudgeAction(juryConfigFetcher, "judgeUpdateJuryConfig", {
      entityId: judge.id,
      juryConfig: JSON.stringify(config),
    });
  };

  const handleTitleEdit = () => {
    if (!editedTitle.trim() || editedTitle === judge.title) {
      setIsEditingTitle(false);
      setEditedTitle(judge.title);
      return;
    }

    const trimmed = editedTitle.trim();
    pendingTitleRef.current = trimmed;
    submitJudgeAction(titleFetcher, "updateJudgeTitle", {
      entityId: judge.id,
      title: trimmed,
    });
  };

  const handleDelete = () => {
    submitJudgeAction(deleteFetcher, "judgeDelete", {
      entityId: judge.id,
    });
  };

  const handleRunJudgeTest = async () => {
    setIsTesting(true);
    setTestResult(null);
    setTestError(null);

    try {
      const body: JudgeTesterRequest = {
        judgeId: judge.id,
        input: testInput,
        actualOutput: testOutput,
        expectedOutput: testExpected || null,
        ruleText: testRule || null,
        juryDepth: 0,
        disableJury: false,
      };

      const result = await invokeFunction(JudgeTesterContract, {
        client: supabase,
        body,
      });

      if (!result.ok) {
        setTestError(formatSupabaseFunctionError(result.error));
        return;
      }

      setTestResult(result.data);
    } catch (err) {
      const parsed = await parseSupabaseFunctionError(err);
      setTestError(formatSupabaseFunctionError(parsed));
    } finally {
      setIsTesting(false);
    }
  };

  useEffect(() => {
    if (systemPromptFetcher.state !== "idle" || !systemPromptFetcher.data)
      return;
    if (systemPromptResultRef.current === systemPromptFetcher.data) return;
    systemPromptResultRef.current = systemPromptFetcher.data;

    if (systemPromptFetcher.data.ok) {
      toast({
        title: "Success",
        description: "System prompt updated successfully",
      });
      setSystemPromptDialogOpen(false);
      revalidator.revalidate();
      onUpdate?.();
    } else {
      toast({
        title: "Error",
        description:
          systemPromptFetcher.data.error || "Failed to update system prompt",
        variant: "destructive",
      });
    }
  }, [
    systemPromptFetcher.state,
    systemPromptFetcher.data,
    toast,
    revalidator,
    onUpdate,
  ]);

  useEffect(() => {
    if (duplicateFetcher.state !== "idle" || !duplicateFetcher.data) return;
    if (duplicateResultRef.current === duplicateFetcher.data) return;
    duplicateResultRef.current = duplicateFetcher.data;

    if (duplicateFetcher.data.ok) {
      toast({ title: "Success", description: "Judge duplicated successfully" });
      setDuplicateDialogOpen(false);
      setDuplicateJudgeName(`${judge.title} (Copy)`);
      revalidator.revalidate();
      onUpdate?.();
    } else {
      toast({
        title: "Error",
        description: duplicateFetcher.data.error || "Failed to duplicate judge",
        variant: "destructive",
      });
    }
  }, [
    duplicateFetcher.state,
    duplicateFetcher.data,
    toast,
    revalidator,
    onUpdate,
    judge.title,
  ]);

  useEffect(() => {
    if (visibilityFetcher.state !== "idle" || !visibilityFetcher.data) return;
    if (visibilityResultRef.current === visibilityFetcher.data) return;
    visibilityResultRef.current = visibilityFetcher.data;

    if (visibilityFetcher.data.ok) {
      if (pendingVisibility !== null) {
        setIsPublic(pendingVisibility);
      }
      toast({
        title: "Success",
        description: `Judge is now ${pendingVisibility ? "public" : "private"}`,
      });
      setPendingVisibility(null);
      revalidator.revalidate();
      onUpdate?.();
    } else {
      setPendingVisibility(null);
      toast({
        title: "Error",
        description:
          visibilityFetcher.data.error || "Failed to update judge visibility",
        variant: "destructive",
      });
    }
  }, [
    visibilityFetcher.state,
    visibilityFetcher.data,
    pendingVisibility,
    toast,
    revalidator,
    onUpdate,
  ]);

  useEffect(() => {
    if (juryConfigFetcher.state !== "idle" || !juryConfigFetcher.data) return;
    if (juryConfigResultRef.current === juryConfigFetcher.data) return;
    juryConfigResultRef.current = juryConfigFetcher.data;

    if (juryConfigFetcher.data.ok) {
      toast({
        title: "Success",
        description: "Jury configuration updated successfully",
      });
      revalidator.revalidate();
      onUpdate?.();
    } else {
      toast({
        title: "Error",
        description:
          juryConfigFetcher.data.error || "Failed to update jury configuration",
        variant: "destructive",
      });
    }
  }, [
    juryConfigFetcher.state,
    juryConfigFetcher.data,
    toast,
    revalidator,
    onUpdate,
  ]);

  useEffect(() => {
    if (titleFetcher.state !== "idle" || !titleFetcher.data) return;
    if (titleResultRef.current === titleFetcher.data) return;
    titleResultRef.current = titleFetcher.data;

    if (titleFetcher.data.ok) {
      const nextTitle = pendingTitleRef.current ?? judge.title;
      toast({
        title: "Success",
        description: "Judge name updated successfully",
      });
      setIsEditingTitle(false);
      setEditedTitle(nextTitle);
      revalidator.revalidate();
      onUpdate?.();
    } else {
      toast({
        title: "Error",
        description: titleFetcher.data.error || "Failed to update judge name",
        variant: "destructive",
      });
      setEditedTitle(judge.title);
      setIsEditingTitle(false);
    }
    pendingTitleRef.current = null;
  }, [
    titleFetcher.state,
    titleFetcher.data,
    toast,
    revalidator,
    onUpdate,
    judge.title,
  ]);

  useEffect(() => {
    if (deleteFetcher.state !== "idle" || !deleteFetcher.data) return;
    if (deleteResultRef.current === deleteFetcher.data) return;
    deleteResultRef.current = deleteFetcher.data;

    if (deleteFetcher.data.ok) {
      toast({ title: "Success", description: "Judge deleted successfully" });
      setDeleteDialogOpen(false);
      revalidator.revalidate();
      onDelete?.();
    } else {
      toast({
        title: "Error",
        description: deleteFetcher.data.error || "Failed to delete judge",
        variant: "destructive",
      });
    }
  }, [deleteFetcher.state, deleteFetcher.data, toast, revalidator, onDelete]);

  return (
    <div className="p-8 w-full">
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            {isEditingTitle ? (
              <div className="flex items-center gap-2">
                <Input
                  value={editedTitle}
                  onChange={(e) => setEditedTitle(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") handleTitleEdit();
                    if (e.key === "Escape") {
                      setIsEditingTitle(false);
                      setEditedTitle(judge.title);
                    }
                  }}
                  className="text-3xl font-bold h-12 w-96"
                  autoFocus
                  aria-label="Judge title"
                />
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={handleTitleEdit}
                  className="h-10 w-10 p-0"
                  aria-label="Save judge title"
                  disabled={isTitlePending}
                >
                  <Check className="h-5 w-5" />
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => {
                    setIsEditingTitle(false);
                    setEditedTitle(judge.title);
                  }}
                  className="h-10 w-10 p-0"
                  aria-label="Cancel judge title edit"
                >
                  <X className="h-5 w-5" />
                </Button>
              </div>
            ) : (
              <>
                <h1 className="text-3xl font-bold text-foreground">
                  {judge.title}
                </h1>
                {isOwner && (
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => setIsEditingTitle(true)}
                    className="h-8 w-8 p-0"
                    aria-label="Edit judge title"
                  >
                    <Pencil className="h-4 w-4" />
                  </Button>
                )}
              </>
            )}
          </div>
          <div className="flex items-center gap-2">
            <Dialog
              open={duplicateDialogOpen}
              onOpenChange={setDuplicateDialogOpen}
            >
              <DialogTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  className="flex items-center gap-2"
                >
                  <Copy className="h-4 w-4" />
                  Duplicate Judge
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Duplicate Judge</DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                  <div>
                    <Label htmlFor="duplicate-name">New Judge Name</Label>
                    <Input
                      id="duplicate-name"
                      value={duplicateJudgeName}
                      onChange={(e) => setDuplicateJudgeName(e.target.value)}
                      placeholder="Enter name for duplicated judge..."
                      className="mt-2"
                    />
                  </div>
                  <div className="flex justify-end gap-2">
                    <Button
                      variant="outline"
                      onClick={() => setDuplicateDialogOpen(false)}
                    >
                      Cancel
                    </Button>
                    <Button
                      onClick={handleDuplicateJudge}
                      disabled={
                        !duplicateJudgeName.trim() || isDuplicatePending
                      }
                    >
                      Duplicate
                    </Button>
                  </div>
                </div>
              </DialogContent>
            </Dialog>
            {isOwner && (
              <Button
                onClick={() => setDeleteDialogOpen(true)}
                variant="outline"
                size="sm"
                className="flex items-center gap-2 text-destructive hover:text-destructive"
              >
                <Trash2 className="h-4 w-4" />
                Delete
              </Button>
            )}
          </div>
        </div>

        <p className="text-muted-foreground">Judge Overview</p>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <div className="bg-card border border-border rounded-lg p-6">
            <h3 className="text-sm font-medium text-muted-foreground mb-2">
              Owner
            </h3>
            <p className="text-lg text-foreground">
              {judge.owner_email || userEmail || "Unknown"}
            </p>
          </div>

          <div className="bg-card border border-border rounded-lg p-6">
            <h3 className="text-sm font-medium text-muted-foreground mb-2">
              Created
            </h3>
            <p className="text-lg text-foreground">
              {formatDistanceToNow(new Date(judge.created_at), {
                addSuffix: true,
              })}
            </p>
            <p className="text-sm text-muted-foreground mt-1">
              {new Date(judge.created_at).toLocaleDateString()}
            </p>
          </div>

          <div className="bg-card border border-border rounded-lg p-6">
            <h3 className="text-sm font-medium text-muted-foreground mb-2">
              Last Updated
            </h3>
            <p className="text-lg text-foreground">
              {formatDistanceToNow(new Date(judge.updated_at), {
                addSuffix: true,
              })}
            </p>
            <p className="text-sm text-muted-foreground mt-1">
              {new Date(judge.updated_at).toLocaleDateString()}
            </p>
          </div>
        </div>

        <div className="bg-card border border-border rounded-lg p-6">
          <h3 className="text-lg font-semibold text-foreground mb-4">
            Judge Configuration
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Judge ID:</span>
              <span className="text-foreground font-mono">{judge.id}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Status:</span>
              <span className="text-foreground">Active</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">AI Model:</span>
              <span className="text-foreground">{judge.model}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Max Tokens:</span>
              <span className="text-foreground">{judge.max_tokens}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Temperature:</span>
              <span className="text-foreground">{judge.temperature}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">OpenRouter:</span>
              <span className="text-foreground">Configured by system</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Jury:</span>
              <span className="text-foreground">
                {juryEnabled ? "Enabled" : "Disabled"}
              </span>
            </div>
            <div className="flex justify-between items-center md:col-span-2">
              <div className="flex items-center gap-2">
                <span className="text-muted-foreground">Visibility:</span>
                {isPublic ? (
                  <Badge variant="default" className="flex items-center gap-1">
                    <Globe className="h-3 w-3" />
                    Public
                  </Badge>
                ) : (
                  <Badge
                    variant="secondary"
                    className="flex items-center gap-1"
                  >
                    <Lock className="h-3 w-3" />
                    Private
                  </Badge>
                )}
              </div>
              <div className="flex items-center gap-2">
                <Label
                  htmlFor="public-switch"
                  className="text-sm cursor-pointer"
                >
                  {isPublic ? "Public" : "Private"}
                </Label>
                <Switch
                  id="public-switch"
                  checked={pendingVisibility ?? isPublic}
                  onCheckedChange={handlePublicToggle}
                  disabled={!isOwner || isVisibilityPending}
                />
              </div>
            </div>
          </div>

          <div className="mt-6 pt-6 border-t border-border">
            <h4 className="text-sm font-medium text-muted-foreground mb-3">
              OpenRouter
            </h4>
            <div className="flex items-center justify-between gap-6">
              <div className="flex items-center gap-3">
                <Badge variant="default" className="flex items-center gap-1">
                  <CheckCircle className="h-3 w-3" />
                  Configured by system
                </Badge>
                <span className="text-sm text-muted-foreground">
                  OpenRouter API access is configured globally for this
                  deployment.
                </span>
              </div>
              {isOwner && (
                <Button
                  onClick={() => setCredentialsDialogOpen(true)}
                  variant="outline"
                  size="sm"
                  className="flex items-center gap-2 shrink-0"
                >
                  <Settings className="h-4 w-4" />
                  Model Settings
                </Button>
              )}
            </div>
          </div>
        </div>

        <div className="bg-card border border-border rounded-lg p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-foreground">
              System Prompt
            </h3>
            {isOwner && (
              <Dialog
                open={systemPromptDialogOpen}
                onOpenChange={setSystemPromptDialogOpen}
              >
                <DialogTrigger asChild>
                  <Button
                    variant="outline"
                    size="sm"
                    className="flex items-center gap-2"
                  >
                    <Edit className="h-4 w-4" />
                    Edit
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-2xl">
                  <DialogHeader>
                    <DialogTitle>Edit System Prompt</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4">
                    <div>
                      <Label htmlFor="system-prompt">System Prompt</Label>
                      <Textarea
                        id="system-prompt"
                        value={systemPrompt}
                        onChange={(e) => setSystemPrompt(e.target.value)}
                        placeholder="Enter the system prompt that will be used for this judge..."
                        className="min-h-[200px] mt-2"
                      />
                    </div>
                    <div className="flex justify-end gap-2">
                      <Button
                        variant="outline"
                        onClick={() => setSystemPromptDialogOpen(false)}
                      >
                        Cancel
                      </Button>
                      <Button
                        onClick={handleSystemPromptSave}
                        disabled={isSystemPromptPending}
                      >
                        Save
                      </Button>
                    </div>
                  </div>
                </DialogContent>
              </Dialog>
            )}
          </div>
          <div className="bg-muted rounded-lg p-4">
            <p className="text-sm text-foreground whitespace-pre-wrap">
              {judge.system_prompt || "No system prompt set"}
            </p>
          </div>
        </div>

        <div className="bg-card border border-border rounded-lg p-6">
          <h3 className="text-lg font-semibold text-foreground mb-4">
            Jury Evaluation
          </h3>
          <div className="space-y-4">
            <div className="flex items-center justify-between gap-6">
              <div className="space-y-1">
                <p className="text-sm text-foreground">Enable jury mode</p>
                <p className="text-xs text-muted-foreground">
                  When enabled, this judge can consult other judges and return a
                  majority score (0 or 100).
                </p>
              </div>
              <Switch
                checked={juryEnabled}
                onCheckedChange={setJuryEnabled}
                disabled={!isOwner || isJuryConfigPending}
              />
            </div>

            <div className="space-y-2">
              <Label>Member judges</Label>
              <p className="text-xs text-muted-foreground">
                Select an odd number of seats. This judge will not vote.
              </p>
              <p className="text-xs text-muted-foreground">
                Total seats:{" "}
                {Object.values(juryMemberSeatCounts).reduce(
                  (sum, count) => sum + Math.max(0, Math.trunc(count)),
                  0,
                )}
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                {availableJudges
                  .filter((candidate) => candidate.id !== judge.id)
                  .map((candidate) => {
                    const seatCount = Math.max(
                      0,
                      Math.trunc(juryMemberSeatCounts[candidate.id] ?? 0),
                    );

                    return (
                      <div
                        key={candidate.id}
                        className="flex items-center gap-3 rounded-md border border-border p-2"
                      >
                        <div className="min-w-0 flex-1">
                          <div className="text-sm text-foreground truncate">
                            {candidate.title}
                          </div>
                          <div className="text-xs text-muted-foreground truncate font-mono">
                            {candidate.id}
                          </div>
                        </div>
                        <Badge
                          variant={candidate.is_public ? "default" : "secondary"}
                          className="shrink-0"
                        >
                          {candidate.is_public ? "Public" : "Private"}
                        </Badge>
                        <div className="flex items-center gap-2 shrink-0">
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            className="h-8 w-8 p-0"
                            onClick={() =>
                              setJuryMemberSeatCount(
                                candidate.id,
                                seatCount - 1,
                              )
                            }
                            disabled={
                              !isOwner || isJuryConfigPending || seatCount <= 0
                            }
                            aria-label={`Decrease seats for ${candidate.title}`}
                          >
                            −
                          </Button>
                          <Input
                            type="number"
                            min={0}
                            step={1}
                            value={seatCount}
                            onChange={(e) =>
                              setJuryMemberSeatCount(
                                candidate.id,
                                Number(e.target.value),
                              )
                            }
                            disabled={!isOwner || isJuryConfigPending}
                            className="h-8 w-16 text-center"
                            aria-label={`Seat count for ${candidate.title}`}
                          />
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            className="h-8 w-8 p-0"
                            onClick={() =>
                              setJuryMemberSeatCount(
                                candidate.id,
                                seatCount + 1,
                              )
                            }
                            disabled={!isOwner || isJuryConfigPending}
                            aria-label={`Increase seats for ${candidate.title}`}
                          >
                            +
                          </Button>
                        </div>
                      </div>
                    );
                  })}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="jury-majority-count">Majority Count</Label>
                <Input
                  id="jury-majority-count"
                  type="number"
                  min={1}
                  value={juryMajorityCount}
                  onChange={(e) => setJuryMajorityCount(Number(e.target.value))}
                  disabled={!isOwner || isJuryConfigPending}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="jury-max-consults">Max Consults</Label>
                <Input
                  id="jury-max-consults"
                  type="number"
                  min={1}
                  value={juryMaxConsults}
                  onChange={(e) => setJuryMaxConsults(Number(e.target.value))}
                  disabled={!isOwner || isJuryConfigPending}
                />
              </div>
            </div>

            {isOwner && (
              <div className="flex justify-end">
                <Button
                  onClick={handleSaveJuryConfig}
                  disabled={isJuryConfigPending}
                >
                  Save Jury Config
                </Button>
              </div>
            )}
          </div>
        </div>

        <Card className="border border-border">
          <CardHeader>
            <CardTitle>Judge Test</CardTitle>
            <CardDescription>
              Run this judge with structured-output scoring (no results are
              saved).
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="judge-test-input">Input *</Label>
                <Textarea
                  id="judge-test-input"
                  value={testInput}
                  onChange={(e) => setTestInput(e.target.value)}
                  placeholder="Enter the test input"
                  className="min-h-[120px]"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="judge-test-output">Candidate Output *</Label>
                <Textarea
                  id="judge-test-output"
                  value={testOutput}
                  onChange={(e) => setTestOutput(e.target.value)}
                  placeholder="Enter the model output to score"
                  className="min-h-[120px]"
                />
              </div>
            </div>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="judge-test-expected">
                  Expected Output (optional)
                </Label>
                <Textarea
                  id="judge-test-expected"
                  value={testExpected}
                  onChange={(e) => setTestExpected(e.target.value)}
                  placeholder="Expected output if applicable"
                  className="min-h-[80px]"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="judge-test-rule">Rule Text (optional)</Label>
                <Textarea
                  id="judge-test-rule"
                  value={testRule}
                  onChange={(e) => setTestRule(e.target.value)}
                  placeholder="Policy or rule for scoring"
                  className="min-h-[80px]"
                />
              </div>
            </div>

            <div className="flex items-center gap-3">
              <Button
                onClick={handleRunJudgeTest}
                disabled={isTesting || !testInput.trim() || !testOutput.trim()}
                className="flex items-center gap-2"
              >
                <Play className="h-4 w-4" />
                {isTesting ? "Running..." : "Run Judge"}
              </Button>
              {testError && (
                <p className="text-sm text-destructive">{testError}</p>
              )}
            </div>

            {testResult && (
              <div className="rounded-lg border border-border p-4 space-y-2">
                <div className="flex items-center gap-2">
                  <Badge variant="default">Score: {testResult.score}</Badge>
                </div>
                <p className="text-sm text-muted-foreground whitespace-pre-wrap break-words">
                  {testResult.explanation.trim() || "No explanation provided."}
                </p>
                {testResult.votes.length > 0 && (
                  <div className="space-y-2">
                    <p className="text-xs text-muted-foreground">Votes</p>
                    {testResult.votes.map((vote, index) => (
                      <div
                        key={`${vote.judgeId}-${index}`}
                        className="space-y-1"
                      >
                        <div className="flex items-center justify-between gap-3 text-xs">
                          <span className="font-mono text-muted-foreground truncate">
                            {vote.judgeId}
                          </span>
                          <Badge variant="secondary" className="shrink-0">
                            {vote.score}
                          </Badge>
                        </div>
                        <p className="text-xs text-muted-foreground whitespace-pre-wrap break-words">
                          {vote.explanation.trim() || "No explanation provided."}
                        </p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <JudgeCredentialsDialog
        judge={judge}
        open={credentialsDialogOpen}
        onOpenChange={setCredentialsDialogOpen}
        onUpdate={() => onUpdate?.()}
      />

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Judge</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this judge? This action cannot be
              undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              disabled={isDeletePending}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
